import type { EventRaw, SpillEvent } from './types';

export type TidligSpillEvent = SpillEvent & {
  trigger: {
    dag: number;
    prioritet?: 'lav' | 'middels' | 'hoy';
  } & SpillEvent['trigger'];
  betingelse?: string;
};

type TidligSpillRaw = EventRaw & {
  betingelse?: string;
};

export class TidligSpillKatalog {
  private _events: TidligSpillEvent[] = [];
  private _loaded = false;

  loadFromArray(raw: TidligSpillRaw[]): void {
    const filtrert = raw
      .filter((e): e is TidligSpillRaw => !!e && !!e.id && !!e.tittel)
      .filter((e) => Array.isArray(e.valg) && e.valg.length >= 1)
      .filter(
        (e) => !!e.trigger && typeof (e.trigger as { dag?: number }).dag === 'number',
      );
    this._events = filtrert.map((e) => this.normaliser(e));
    this._loaded = true;
  }

  get isReady(): boolean {
    return this._loaded;
  }

  get events(): TidligSpillEvent[] {
    return this._events.map((e) => this.clone(e));
  }

  getByDay(dag: number): TidligSpillEvent | null {
    const treff = this._events.find((e) => e.trigger?.dag === dag);
    return treff ? this.clone(treff) : null;
  }

  findById(id: string): TidligSpillEvent | null {
    const e = this._events.find((x) => x.id === id);
    return e ? this.clone(e) : null;
  }

  private normaliser(raw: TidligSpillRaw): TidligSpillEvent {
    const trigger = (raw.trigger ?? {}) as TidligSpillEvent['trigger'];
    const event: TidligSpillEvent = {
      id: raw.id,
      kategori: raw.kategori ?? 'tidlig-spill',
      tittel: raw.tittel,
      beskrivelse: raw.beskrivelse,
      trigger: { ...trigger, dag: trigger.dag ?? 0 },
      valg: raw.valg.map((v) => ({
        tekst: v.tekst,
        konsekvenser: { ...v.konsekvenser },
      })),
    };
    if (typeof raw.betingelse === 'string' && raw.betingelse.trim().length > 0) {
      event.betingelse = raw.betingelse.trim();
    }
    return event;
  }

  private clone(e: TidligSpillEvent): TidligSpillEvent {
    return {
      ...e,
      trigger: { ...e.trigger },
      valg: e.valg.map((v) => ({ tekst: v.tekst, konsekvenser: { ...v.konsekvenser } })),
    };
  }
}

export const tidligSpillKatalog = new TidligSpillKatalog();

export type TidligSpillBetingelseKontekst = {
  flags: ReadonlyArray<string>;
};

export function tidligSpillBetingelseOk(
  uttrykk: string | undefined,
  ctx: TidligSpillBetingelseKontekst,
): boolean {
  if (!uttrykk || uttrykk.trim().length === 0) return true;
  const trimmet = uttrykk.trim();

  // ikke_flag_set('navn')
  const ikkeFlag = trimmet.match(/^ikke_flag_set\(\s*['"]([\w-]+)['"]\s*\)$/);
  if (ikkeFlag) {
    return !ctx.flags.includes(ikkeFlag[1]);
  }

  // flag_set('navn')
  const flag = trimmet.match(/^flag_set\(\s*['"]([\w-]+)['"]\s*\)$/);
  if (flag) {
    return ctx.flags.includes(flag[1]);
  }

  // Ukjent uttrykk — vær konservativ, ikke trigger
  console.warn(`Ukjent tidlig-spill-betingelse: «${trimmet}»`);
  return false;
}
